import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SellFormData } from "@/hooks/forms/useSellForm";
import { MilesProgram } from "@/lib/miles-calculations";
import { cn } from "@/lib/utils";
import { Check, Eye, Lock } from "lucide-react";
import { useState } from "react";
import { Control, FieldErrors, FieldNamesMarkedBoolean } from "react-hook-form";

export function SellCredentialsSection({
  control,
  errors,
  touchedFields,
  program,
}: {
  control: Control<SellFormData>;
  errors: FieldErrors<SellFormData>;
  touchedFields: Partial<Readonly<FieldNamesMarkedBoolean<SellFormData>>>;
  program: Pick<
    MilesProgram,
    "name" | "usernameLabelOverride" | "passwordLabelOverride"
  >;
}) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <section className="grid grid-cols-1 gap-5 rounded-md border border-blue-100 bg-blue-50/50 p-4 xl:grid-cols-[48%_1fr] xl:items-center xl:gap-8">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-100 text-blue-700">
          <Lock className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 xl:max-w-[32rem]">
          <h2 className="text-sm font-semibold text-gray-900 xl:text-base">
            {program.name} Account Access
          </h2>
          <p className="mt-2 text-xs leading-relaxed text-gray-600 xl:text-sm">
            We need temporary access to verify your miles balance and process
            your payout. We never make changes without your explicit approval
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-2">
          <FormField
            name="frequentFlyerNumber"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {program.usernameLabelOverride
                    ? `${program.usernameLabelOverride} *`
                    : "Username *"}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder={`Enter your ${program.usernameLabelOverride ? `${program.usernameLabelOverride.toLowerCase()}` : "username"}`}
                      className={cn(
                        "text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500",
                        errors.frequentFlyerNumber &&
                          "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      {...field}
                    />
                    {!errors.frequentFlyerNumber &&
                      field.value &&
                      touchedFields.frequentFlyerNumber && (
                        <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-green-500" />
                      )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-2 flex items-end w-full">
          <FormField
            name="accountPassword"
            control={control}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>
                  {program.passwordLabelOverride
                    ? `${program.passwordLabelOverride} *`
                    : "Password *"}
                </FormLabel>
                <FormControl className="w-full">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={`Enter your ${program.passwordLabelOverride ? `${program.passwordLabelOverride.toLowerCase()}` : "password"}`}
                      className={cn(
                        "text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500",
                        errors.accountPassword &&
                          "border-red-500 focus:border-red-500 focus:ring-red-500",
                      )}
                      {...field}
                    />
                    {!errors.accountPassword &&
                      field.value &&
                      touchedFields.accountPassword && (
                        <Check className="absolute right-10 top-1/2 h-4 w-4 -translate-y-1/2 transform text-green-500" />
                      )}
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                      onTouchStart={() => setShowPassword(true)}
                      onTouchEnd={() => setShowPassword(false)}
                      onMouseDown={() => setShowPassword(true)}
                      onMouseUp={() => setShowPassword(false)}
                      onMouseLeave={() => setShowPassword(false)}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </section>
  );
}
